import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { GRN } from 'src/models/Management.interface';
import { apiUrls, errorMessages, pagination } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { dateFormatYYYYMMDD } from 'src/utils/format.utils';

interface PaginatedGRNResponse {
  data: GRN[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

interface GRNFetchParams {
  offset: number;
  limit: number;
  search: string;
  sortBy: string;
  order: 'ASC' | 'DESC';
  status?: string;
  vendorId?: string;
  startDate?: string;
  endDate?: string;
}

export const useGetGRN = () => {
  const [grns, setGrns] = useState<GRN[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(pagination.DEFAULT_PAGE);
  const [limit, setLimit] = useState(pagination.DEFAULT_PAGE_SIZE);
  const { showSnackbar } = StyledSnackbar();

  const fetchGRNs = async (
    page = 1,
    limit = 15,
    search = '',
    sortBy = 'createdAt',
    order: 'ASC' | 'DESC' = 'DESC',
    status?: string,
    vendorId?: string,
    fromDate?: Date | null,
    toDate?: Date | null
  ) => {
    setLoading(true);
    try {
      const params: GRNFetchParams = {
        offset: page,
        limit: limit,
        search: search,
        sortBy: sortBy,
        order: order,
      };

      if (status) params.status = status;
      if (vendorId) params.vendorId = vendorId;
      if (fromDate) params.startDate = dateFormatYYYYMMDD(fromDate);
      if (toDate) params.endDate = dateFormatYYYYMMDD(toDate);

      const response = await axiosTokenInstance.get<PaginatedGRNResponse>(
        apiUrls.GRN_MANAGEMENT,
        {
          params,
          withCredentials: true,
        }
      );

      setGrns(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(Math.ceil(response.data.totalItems / limit));
      setCurrentPage(page);
    } catch (error: any) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      setGrns([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const fetchGRNById = async (id: string): Promise<GRN | null> => {
    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_GRN_BY_ID, {
        params: { id },
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      return null;
    }
  };

  const deleteGRN = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(apiUrls.DELETE_GRN, {
        params: { id },
        withCredentials: true,
      });

      setGrns((prevGrns) => prevGrns.filter((grn) => grn.id !== id));
      showSnackbar(response.data.message, 'success');
    } catch (error: any) {
      showSnackbar(error.message, 'error');
    }
  };

  return {
    loading,
    grns,
    totalItems,
    totalPages,
    currentPage,
    setCurrentPage,
    limit,
    setLimit,
    fetchGRNs,
    fetchGRNById,
    deleteGRN,
  };
};
